import React, { useEffect, useState } from "react";
import { X, Sparkles, ChevronRight } from "lucide-react";

/**
 * "What's New" modal — shown once per version to returning users.
 * Bump WHATS_NEW_VERSION when you add a new entry at the top of ENTRIES.
 * Users who dismissed a prior version will see the modal ONE more time.
 */
const WHATS_NEW_VERSION = "2026.02.05";
const STORAGE_KEY = "adn.lastSeenWhatsNew";

const ENTRIES = [
  {
    version: "2026.02.05",
    date: "Feb 5, 2026",
    highlights: [
      { title: "Nighttime quiet hours", body: "Pause push + SMS notifications between the times you set. Email + your in-app inbox still deliver, so you'll never miss a winner alert." },
      { title: "New app icon + splash screen", body: "Fresh FanFest branding with the neon badge and animated boot experience." },
      { title: "Notification timestamps", body: "Every message in your inbox now shows when it was sent (\"3m ago\", \"Yesterday 4:12 PM\")." },
      { title: "Lock-screen push reliability", body: "You'll now receive messages even when the app is closed — as long as you've installed to Home Screen and enabled push." },
      { title: "Forgot password", body: "Self-service password reset — no more waiting on support." },
    ],
  },
];

export default function WhatsNew() {
  const [open, setOpen] = useState(false);
  const [entry] = useState(ENTRIES[0]);

  useEffect(() => {
    try {
      const last = localStorage.getItem(STORAGE_KEY);
      if (last !== WHATS_NEW_VERSION) {
        // Delay slightly so it doesn't clash with the boot splash fade-out
        const t = setTimeout(() => setOpen(true), 1200);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage unavailable — silently skip */ }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, WHATS_NEW_VERSION); } catch { /* noop */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={dismiss}
      data-testid="whats-new-modal"
    >
      <div
        className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto adn-card p-6 border border-primary/40 shadow-2xl shadow-primary/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={dismiss}
          data-testid="whats-new-close"
          aria-label="Close what's new"
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-white/70 hover:text-white"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-primary">
          <Sparkles size={14} /> What&apos;s new
        </div>
        <h2 className="font-bebas text-4xl mt-1">JUST SHIPPED</h2>
        <div className="text-xs text-white/50 mt-1">v{entry.version} · {entry.date}</div>

        <ul className="mt-6 space-y-4">
          {entry.highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`whats-new-item-${i}`}>
              <ChevronRight size={14} className="text-primary mt-1 shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-sm">{h.title}</div>
                <div className="text-white/70 text-sm mt-0.5 leading-relaxed">{h.body}</div>
              </div>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={dismiss}
          data-testid="whats-new-cta"
          className="mt-8 w-full px-6 py-3 bg-primary text-black font-bebas text-lg rounded-sm hover:brightness-110"
        >
          GOT IT — LET&apos;S GO
        </button>

        <p className="text-[10px] text-white/40 mt-3 text-center">
          You&apos;ll see this once per release. We won&apos;t nag.
        </p>
      </div>
    </div>
  );
}
